import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SettingOutlined, FileTextOutlined, BankOutlined, TeamOutlined,
  IdcardOutlined, CalculatorOutlined, GlobalOutlined, EnvironmentOutlined,
  RightOutlined, AppstoreOutlined, SafetyOutlined, ClockCircleOutlined, MobileOutlined,
  PrinterOutlined, EditOutlined, NotificationOutlined,
} from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import Layout, { LayoutEmbedContext } from '../../components/Layout';
import { avecLibelleTraduit } from '../../i18n/libelles';
import { can, canAny } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';

import ParametragePieces from './ParametragePieces';
import ParametresListe from './ParametresListe';
import ParametresBanques from './ParametresBanques';
import ParametresAttestations from './ParametresAttestations';
import ParametresCima from './ParametresCima';
import ParametresDocuments from './ParametresDocuments';
import ParametresSecurite from './ParametresSecurite';
import ParametresBanniere from './ParametresBanniere';
import ParametresSignatures from './ParametresSignatures';
import ParametresDelais from './ParametresDelais';
import EntitesConstat from './EntitesConstat';
import Pays from './Pays';
import Experts from '../experts/Experts';
import PublierApplication from './PublierApplication';

const GREEN = '#1a6b3c';

/*  Regroupement des paramètres par section  */
const GROUPES = [
  {
    code: 'referentiels',
    items: [
      { key: 'pays', icon: <GlobalOutlined />, Composant: Pays },
      { key: 'entites', icon: <EnvironmentOutlined />, Composant: EntitesConstat },
      { key: 'banques', icon: <BankOutlined />, Composant: ParametresBanques },
      { key: 'listes', icon: <AppstoreOutlined />, Composant: ParametresListe },
      { key: 'experts', icon: <TeamOutlined />, Composant: Experts },
    ],
  },
  {
    code: 'metier',
    items: [
      { key: 'pieces', icon: <FileTextOutlined />, Composant: ParametragePieces },
      { key: 'attestations', icon: <IdcardOutlined />, Composant: ParametresAttestations },
      { key: 'baremes', icon: <CalculatorOutlined />, Composant: ParametresCima },
      { key: 'documents', icon: <PrinterOutlined />, hab: HAB.PARAMETRE_UPDATE, Composant: ParametresDocuments },
      { key: 'delais', icon: <ClockCircleOutlined />, Composant: ParametresDelais },
    ],
  },
  {
    code: 'securite',
    items: [
      { key: 'securite', icon: <SafetyOutlined />, hab: [HAB.ORGANISME_UPDATE, HAB.PARAMETRE_UPDATE], Composant: ParametresSecurite },
      { key: 'banniere', icon: <NotificationOutlined />, hab: HAB.PARAMETRE_UPDATE, Composant: ParametresBanniere },
      { key: 'signatures', icon: <EditOutlined />, hab: HAB.SIGNATURE_PARAMETRER, Composant: ParametresSignatures },
      { key: 'application-mobile', icon: <MobileOutlined />, hab: HAB.APP_MOBILE_PUBLIER, Composant: PublierApplication },
    ],
  },
];

const TOUS = GROUPES.flatMap((g) => g.items);

// Libellé et description de chaque rubrique, traduits à la lecture (mêmes objets que GROUPES).
avecLibelleTraduit(TOUS, 'parametres:layout.rubriques', { cle: 'key' });
avecLibelleTraduit(TOUS, 'parametres:layout.descriptions', { cle: 'key', champ: 'desc' });

// Rubriques visibles selon l'habilitation de l'utilisateur.
// Un item qui declare `hab` est garde par cette habilitation - c'est la regle du
// depot (« une garde d'UI s'ecrit can(HAB.X) - jamais profil === '...' »).
//
// L'ecran de securite etait garde par `roles: ['ADMIN','SE']`, resolu via
// PROFILS_ADMIN - une liste de NOMS de profils qui n'inclut pas SUPERADMIN. Un
// SUPERADMIN ne voyait donc pas l'onglet « Double authentification » alors qu'il
// peut tout le reste : le nom du profil decidait a la place de l'habilitation.
//
// Il en porte deux, parce que l'ecran ecrit a deux endroits que le serveur garde
// separement - ORGANISME_UPDATE pour les interrupteurs, PARAMETRE_UPDATE pour les
// durees de validite. Tenir l'une des deux suffit a ouvrir l'ecran ; l'ecran
// lui-meme desactive la moitie qu'on ne peut pas ecrire, et dit laquelle.
//
// Un item sans `hab` est visible de tous : la rubrique est deja derriere la garde
// de route de /parametres.
const groupesVisibles = () => {
  const autorise = (it) => {
    if (!it.hab) return true;
    return Array.isArray(it.hab) ? canAny(it.hab) : can(it.hab);
  };
  return GROUPES
    .map((g) => ({ ...g, items: g.items.filter(autorise) }))
    .filter((g) => g.items.length > 0);
};

/*  Écran d'accueil (section dynamique par défaut)  */
function Accueil({ onSelect }) {
  const { t } = useTranslation('parametres');
  return (
    <div style={{ maxWidth: 620, margin: '0 auto', textAlign: 'center', padding: '48px 24px' }}>
      <div style={{
        width: 72, height: 72, borderRadius: 18, margin: '0 auto 20px',
        background: '#e8f5ed', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <SettingOutlined style={{ fontSize: 34, color: GREEN }} />
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
        {t('layout.accueil.titre')}
      </h2>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
        <Trans t={t} i18nKey="layout.accueil.intro" components={{ 1: <strong />, 3: <strong /> }} />
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        {TOUS.slice(0, 5).map((it) => (
          <button
            key={it.key}
            onClick={() => onSelect(it.key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
              borderRadius: 20, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
            }}
          >
            {it.icon} {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/*  Page principale  */
export default function ParametreLayout() {
  const { t } = useTranslation('parametres');
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('section');
  const groupes = useMemo(() => groupesVisibles(), []);
  const visibles = useMemo(() => groupes.flatMap((g) => g.items), [groupes]);
  const [active, setActive] = useState(visibles.some((i) => i.key === initial) ? initial : null);

  const current = useMemo(() => visibles.find((i) => i.key === active) || null, [visibles, active]);

  const select = (key) => {
    setActive(key);
    setSearchParams(key ? { section: key } : {}, { replace: true });
  };

  return (
    <Layout title={t('layout.page.titre')}>
      <div className="cb-page-header" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="cb-page-title">{t('layout.page.titre')}</h1>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/*  Menu des paramètres (gauche)  */}
        <aside
          className="cb-card"
          style={{ width: 280, flexShrink: 0, padding: 10, position: 'sticky', top: 72 }}
        >
          {groupes.map((groupe) => (
            <div key={groupe.code} style={{ marginBottom: 10 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
                color: '#9ca3af', padding: '8px 10px 4px',
              }}>
                {t(`layout.groupes.${groupe.code}`)}
              </div>
              {groupe.items.map((it) => {
                const actif = it.key === active;
                return (
                  <button
                    key={it.key}
                    onClick={() => select(it.key)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      borderRadius: 8, padding: '9px 10px', marginBottom: 2,
                      background: actif ? '#e8f5ed' : 'transparent',
                      color: actif ? GREEN : '#374151',
                      fontWeight: actif ? 600 : 500, fontSize: 13.5,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!actif) e.currentTarget.style.background = '#f4f6f5'; }}
                    onMouseLeave={(e) => { if (!actif) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ color: actif ? GREEN : '#9ca3af', display: 'inline-flex', fontSize: 15 }}>
                      {it.icon}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.label}
                    </span>
                    {actif && <RightOutlined style={{ fontSize: 10 }} />}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        {/*  Section dynamique (droite)  */}
        <section style={{ flex: 1, minWidth: 0 }}>
          {current ? (
            <LayoutEmbedContext.Provider value={true}>
              <current.Composant />
            </LayoutEmbedContext.Provider>
          ) : (
            <div className="cb-card" style={{ padding: 0 }}>
              <Accueil onSelect={select} />
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
